import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { useLocales } from '../locales';
import Iconify from './minimal/iconify';

/**
 * Inactivity Warning Dialog
 *
 * Non-blocking warning shown before auto-logout due to inactivity.
 * User can click "Stay Logged In" to reset the timer and continue working.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is visible
 * @param {Function} props.onStayLoggedIn - Callback when user wants to stay logged in
 */
export default function InactivityWarningDialog({ open, onStayLoggedIn }) {
  const { translate } = useLocales();

  return (
    <Dialog
      open={open}
      onClose={onStayLoggedIn} // Allow closing by clicking outside
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 8,
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Iconify
            icon="eva:alert-circle-outline"
            width={28}
            sx={{ color: 'warning.main' }}
          />
          <Typography variant="h6">
            {translate('inactivity.warning_title')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" color="text.secondary">
          {translate('inactivity.warning_message')}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 2, fontStyle: 'italic' }}
        >
          {translate('inactivity.warning_hint')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="contained"
          onClick={onStayLoggedIn}
          size="large"
          fullWidth
          sx={{
            bgcolor: 'text.primary',
            color: (theme) =>
              theme.palette.mode === 'light' ? 'common.white' : 'grey.800',
            '&:hover': {
              bgcolor: 'text.primary',
              opacity: 0.9,
            },
          }}
        >
          {translate('inactivity.stay_logged_in')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

InactivityWarningDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onStayLoggedIn: PropTypes.func.isRequired,
};
